// --------------------------------------------------------------
// Created On: 2021-11-08
// Author: Zachary Thomas
//
// Last Modified: 2026-10-16
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import useApi from "../../../hooks/useApi";
import {
  API,
  CUSTOMER_SERVICE_OVERRIDE_MESSAGE,
  CUSTOMER_SERVICE_PHONE,
  CUSTOMER_SERVICE_EMAIL,
} from "../../../constants/miscellaneous";
import useReduxSelector from "../../../hooks/useReduxSelector";
import Spinner from "../../../components/Spinner/Spinner";
import InfoModal from "../../../components/InfoModal/InfoModal";
import HelpAsset from "../../../components/HelpAsset/HelpAsset";
import userHasPermission from "../../../utilities/userHasPermission";
import { CREATE_ASSETS_PERMISSION } from "../../../constants/permissions";
import styles from "./EmptyDashboardHelp.module.scss";
import AssetManagementWizard from "src/components/AssetManagementWizard/AssetManagementWizard";
import GetDeviceMapResponseBody from "src/types/api/GetDeviceMapResponseBody";

// Overlay and message for users who visit an empty data dashboard.
export default function EmptyDashboardHelp(props: Props): Component {
  const currentUser = useReduxSelector((state) => state.currentUser);
  const [loading, setLoading] = useState<boolean>(false);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [controllers, setControllers] = useState<Controller[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [productManufacturers, setProductManufacturers] = useState<string[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [analogSensors, setAnalogSensors] = useState<AnalogSensor[]>([]);
  const [digitalSensors, setDigitalSensors] = useState<DigitalSensor[]>([]);
  const [monitoringDevices, setMonitoringDevices] = useState<MonitoringDevice[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [gatewayAssets, setGatewayAssets] = useState<Asset[]>([]);
  const [modbusSensors, setModbusSensors] = useState<ModbusSensor[]>([]);
  const [runtimeServiceAttributeMap, setRuntimeServiceAttributeMap] = useState<RuntimeServiceAttributeMap>({});
  const [assetMapIcons, setAssetMapIcons] = useState<AssetMapIcon[]>([]);

  // Get asset supporting data.
  useApi(
    () => {
      if (userHasPermission([[CREATE_ASSETS_PERMISSION]])) {
        setLoading(true);
        return true;
      } else {
        return false;
      }
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/device`,
    },
    async (response: Response, responseBody: GetDeviceMapResponseBody) => {
      if (response.ok && responseBody) {
        // Add the "None" controller to the controller list.
        const noController = {
          controllerId: 0,
          controllerModel: null,
          manufacturerCompanyCode: null,
          name: "",
        };
        setControllers([noController, ...responseBody.controllers]);
        setDeviceTypes(responseBody.deviceTypes);
        setProductManufacturers(responseBody.productManufacturers);
        setProductTypes(responseBody.productTypes);
        setMonitoringDevices(responseBody.monitoringDevices);
        setAnalogSensors(responseBody.analogSensors);
        setDigitalSensors(responseBody.digitalSensors);
        setTemplates(responseBody.templates);
        setAssetMapIcons(responseBody.assetMapIcons);
        setGatewayAssets(responseBody.gatewayAssets);
        setModbusSensors(responseBody.modbusSensors);
        setRuntimeServiceAttributeMap(responseBody.runtimeServiceAttributeMap);
      }
      setLoading(false);
    },
    []
  );

  return (
    <Fragment>
      <Spinner loading={loading} />

      <div className={styles.overlay} />

      <div data-test="empty-dashboard-popup" className={styles.body}>
        {userHasPermission([[CREATE_ASSETS_PERMISSION]]) ? (
          <div>
            <p>You do not have any assets. Would you like to create one?</p>

            <div>
              <InfoModal
                showModal={showInfoModal}
                title="What is an Asset?"
                darkBackground={false}
                linkMessage="( What is an asset? )"
                onClick={() => {
                  setShowCreateModal(false);
                  setShowInfoModal(true);
                }}
                onClose={() => setShowInfoModal(false)}
              >
                <HelpAsset />
              </InfoModal>
            </div>

            <button
              className="btn btn-primary mt-5 mb-2"
              onClick={() => {
                setShowCreateModal(true);
                setShowInfoModal(false);
              }}
            >
              Create Asset
            </button>

            <AssetManagementWizard
              assetId={0}
              showModal={showCreateModal}
              isCreatingNewAsset={true}
              disableDataCollectionSelection={false}
              templates={templates}
              assetMapIcons={assetMapIcons}
              deviceTypes={deviceTypes}
              monitoringDevices={monitoringDevices}
              gatewayAssets={gatewayAssets}
              productTypes={productTypes}
              productManufacturers={productManufacturers}
              controllers={controllers}
              modbusSensors={modbusSensors}
              analogSensors={analogSensors}
              digitalSensors={digitalSensors}
              runtimeServiceAttributeMap={runtimeServiceAttributeMap}
              onClose={() => setShowCreateModal(false)}
              onAction={() => props.onCreateAsset()}
            />
          </div>
        ) : (
          <span className={styles.text}>
            {`It doesn't look like you have permission to view any assets yet.` +
              ` If you think this is an error, please contact one of the '${currentUser.companyName}'` +
              ` administrators. If you are unable to contact an administrator, ` +
              `${
                CUSTOMER_SERVICE_OVERRIDE_MESSAGE === null
                  ? `please contact customer service by email at ${CUSTOMER_SERVICE_EMAIL}` +
                    ` or by phone at ${CUSTOMER_SERVICE_PHONE}.`
                  : `${CUSTOMER_SERVICE_OVERRIDE_MESSAGE}`
              }`}
          </span>
        )}
      </div>
    </Fragment>
  );
}

interface Props {
  onCreateAsset: () => void;
}

interface RuntimeServiceAttributeMap {
  [key: string]: RuntimeServiceAttribute;
}

interface RuntimeServiceAttribute {
  attributeId: number;
  name: string;
  code: string;
}

interface AssetMapIcon {
  assetMapIconId: number;
  name: string;
  code: string;
}

interface MonitoringDevice {
  formId?: number;
  deviceId: number;
  deviceType: string;
  deviceIdentifier: string;
  attributes?: DeviceAttribute[];
  deviceLog?: DeviceLog;
}

interface DeviceAttribute {
  code: string;
  name: string;
}

type DeviceLog = {
  lastUpdated: DeviceLogUnit;
} & {
  [key: string]: DeviceLogUnit;
};

interface DeviceLogUnit {
  code: string;
  name: string;
  value: string | number | boolean;
  latitude?: number;
  longitude?: number;
  unitLong: string;
  unitShort: string;
  unitSymbol: string;
  icon: string;
  isHistorical: boolean;
  sensorConnected?: boolean;
  mostRecentUtc?: string;
}

interface ProductType {
  productTypeId: number;
  name: string;
}

interface Template {
  assetTemplateId: number;
  name: string;
  isImmutable: boolean;
}

interface DeviceType {
  deviceTypeId: number;
  model: string;
  name: string;
  maxAnalogInputCount: number;
  maxDigitalInputCount: number;
  maxBluetoothSensorCount: number;
  maxDigitalOutputCount: number;
  isGatewayEnabled: boolean;
}

interface Controller {
  controllerId: number;
  controllerModel: string | null;
  manufacturerCompanyCode: string | null;
  name: string;
}

interface AnalogSensor {
  analogSensorId: number;
  name: string;
  applications: AnalogApplication[];
}

interface AnalogApplication {
  attributeId: number;
  attributeConnectedId: number;
  name: string;
}

interface DigitalSensor {
  digitalSensorId: number;
  name: string;
  applications: DigitalApplication[];
  hasCounterSupport: boolean;
}

interface DigitalApplication {
  attributeStateId: number;
  attributeFlowId: number;
  attributeAccumulationId: number;
  reportingDescription: string;
}

interface Asset {
  assetId: number;
  name: string;
  nickname: string;
  isRented?: boolean;
  isMigrating: boolean;
  isGateway: boolean;
  lastConfigSuccessful?: boolean;
}

interface ModbusSensor {
  modbusSensorId: number;
  name: string;
  maxCount: number;
}
